import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Server, Shield, Bell, Download, RefreshCw, Save, AlertTriangle, CheckCircle, Code } from 'lucide-react';
import axios from 'axios';
import Toggle from '../components/Toggle';
import ConfigRow from '../components/ConfigRow';
import { useAuth } from '../contexts/AuthContext';

interface SystemHealth {
  status: string;
  uptime_seconds: number;
  database_status: string;
  redis_status: string;
  active_connections: number;
  memory_usage_percent: number;
  cpu_usage_percent: number;
}

interface SystemSettings {
  app_name: string;
  app_version: string;
  max_file_size: number;
  session_timeout: number;
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
  whatsapp_notifications: boolean;
  maintenance_mode: boolean;
  maintenance_message: string;
  registration_enabled: boolean;
  auto_backup_enabled: boolean;
  backup_frequency: number;
  backup_retention_days: number;
  rate_limit_per_minute: number;
  auth_rate_limit_per_minute: number;
  max_login_attempts: number;
  lockout_duration_minutes: number;
  smtp_provider: string;
  email_from: string;
}

interface ApiConfig {
  debug: boolean;
  access_token_expire_minutes: number;
  refresh_token_expire_days: number;
  remember_me_expire_days: number;
  location_update_interval: number;
  location_history_days: number;
  apns_enabled: boolean;
  apns_use_sandbox: boolean;
  apns_bundle_id: string;
  fcm_enabled: boolean;
  whatsapp_enabled: boolean;
  smtp_provider: string;
  email_from: string;
  database_url: string;
  redis_url: string;
  allowed_origins: string[];
  algorithm: string;
}

const Settings: React.FC = () => {
  const { token } = useAuth();
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [settings, setSettings] = useState<SystemSettings>({
    app_name: 'LifeShare',
    app_version: '2.0.0',
    max_file_size: 50,
    session_timeout: 525600,
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
    whatsapp_notifications: true,
    maintenance_mode: false,
    maintenance_message: '',
    registration_enabled: true,
    auto_backup_enabled: true,
    backup_frequency: 24,
    backup_retention_days: 30,
    rate_limit_per_minute: 60,
    auth_rate_limit_per_minute: 10,
    max_login_attempts: 5,
    lockout_duration_minutes: 15,
    smtp_provider: 'godaddy',
    email_from: 'copyright@medianov.com',
  });
  const [apiConfig, setApiConfig] = useState<ApiConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'notifications' | 'security' | 'api' | 'system'>('general');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSystemHealth();
    fetchSettings();
    fetchApiConfig();
    const healthInterval = setInterval(fetchSystemHealth, 30000);
    return () => clearInterval(healthInterval);
  }, []);

  const fetchSystemHealth = async () => {
    try {
      const response = await axios.get('https://api.lifeshare.app/api/v1/admin/health', { headers });
      setHealth(response.data);
    } catch (error) {
      setHealth({ status: 'error', uptime_seconds: 0, database_status: 'error', redis_status: 'error', active_connections: 0, memory_usage_percent: 0, cpu_usage_percent: 0 });
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('https://api.lifeshare.app/api/v1/admin/settings', { headers });
      setSettings(response.data);
    } catch (error) {
      console.error('Settings fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiConfig = async () => {
    try {
      const response = await axios.get('https://api.lifeshare.app/api/v1/admin/api-config', { headers });
      setApiConfig(response.data);
    } catch (error) {
      console.error('API config fetch error:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const response = await axios.put('https://api.lifeshare.app/api/v1/admin/settings', settings, { headers });
      setSettings(response.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error: any) {
      alert('Ayarlar kaydedilemedi: ' + (error.response?.data?.detail || 'Bir hata oluştu'));
    } finally {
      setSaving(false);
    }
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}g ${hours}s`;
    if (hours > 0) return `${hours}s ${minutes}d`;
    return `${minutes}d`;
  };

  const getHealthColor = (status: string) => {
    if (status === 'healthy') return 'bg-green-100 text-green-700';
    if (status === 'warning') return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const tabs = [
    { id: 'general', label: 'Genel', icon: SettingsIcon },
    { id: 'notifications', label: 'Bildirim', icon: Bell },
    { id: 'security', label: 'Güvenlik', icon: Shield },
    { id: 'api', label: 'API & Config', icon: Code },
    { id: 'system', label: 'Sistem', icon: Server }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Ayarlar</h1>
            <p className="text-sm text-gray-500 mt-1">Sistem konfigürasyonu v{settings.app_version}</p>
          </div>
          <button onClick={handleSaveSettings} disabled={saving} className={`px-4 py-2.5 text-white rounded-xl transition-colors flex items-center gap-2 ${saved ? 'bg-green-600' : 'bg-purple-600 hover:bg-purple-700'}`}>
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {saved ? 'Kaydedildi' : 'Kaydet'}
          </button>
        </div>

        {/* System Health */}
        {health && (
          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">Sistem Durumu</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getHealthColor(health.status)}`}>
                {health.status === 'healthy' ? '✓ Sağlıklı' : health.status === 'warning' ? '⚠ Uyarı' : '✕ Hata'}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <p className="text-xl font-bold text-purple-600">{formatUptime(health.uptime_seconds)}</p>
                <p className="text-xs text-gray-500">Çalışma Süresi</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <p className="text-xl font-bold text-blue-600">{health.active_connections}</p>
                <p className="text-xs text-gray-500">Aktif Bağlantı</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <p className="text-xl font-bold text-orange-600">{health.memory_usage_percent.toFixed(1)}%</p>
                <p className="text-xs text-gray-500">Bellek</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <p className="text-xl font-bold text-green-600">{health.cpu_usage_percent.toFixed(1)}%</p>
                <p className="text-xs text-gray-500">CPU</p>
              </div>
            </div>
          </div>
        )}

        {/* Maintenance Banner */}
        {settings.maintenance_mode && (
          <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-orange-800">Bakım Modu Aktif</p>
              <p className="text-xs text-orange-600">Tüm kullanıcı API istekleri 503 ile bloklanıyor. Sadece admin paneli çalışıyor.</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-2xl p-2 flex gap-1 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-purple-100 text-purple-700' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl p-6">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Genel Ayarlar</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Uygulama Adı</label>
                  <input type="text" value={settings.app_name} onChange={(e) => setSettings({ ...settings, app_name: e.target.value })} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Maksimum Dosya Boyutu (MB)</label>
                  <input type="number" value={settings.max_file_size} onChange={(e) => setSettings({ ...settings, max_file_size: parseInt(e.target.value) || 0 })} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500" />
                </div>
              </div>

              <div className="space-y-3">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <Toggle enabled={settings.registration_enabled} onChange={(v) => setSettings({ ...settings, registration_enabled: v })} label="Yeni Kayıt" description="Yeni kullanıcı kaydına izin ver. Kapatıldığında kayıt endpoint'i 503 döner." />
                </div>
                <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                  <Toggle enabled={settings.maintenance_mode} onChange={(v) => setSettings({ ...settings, maintenance_mode: v })} label="Bakım Modu" description="Aktifken tüm kullanıcı API istekleri bloklanır. Sadece admin paneli çalışır." />
                  {settings.maintenance_mode && (
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-orange-700 mb-1">Bakım Mesajı</label>
                      <input type="text" value={settings.maintenance_message} onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })} placeholder="Sistem bakımda, lütfen daha sonra tekrar deneyin." className="w-full px-3 py-2 bg-white rounded-lg text-sm outline-none border border-orange-200" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Bildirim Ayarları</h2>
              <div className="space-y-3">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <Toggle enabled={settings.push_notifications} onChange={(v) => setSettings({ ...settings, push_notifications: v })} label="Push Bildirimleri" description="iOS/Android mobil uygulama push bildirimleri (APNs)" />
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <Toggle enabled={settings.email_notifications} onChange={(v) => setSettings({ ...settings, email_notifications: v })} label="Email Bildirimleri" description="OTP, şifre sıfırlama, hesap silme, premium bildirimleri" />
                </div>
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <Toggle enabled={settings.whatsapp_notifications} onChange={(v) => setSettings({ ...settings, whatsapp_notifications: v })} label="WhatsApp Bildirimleri" description="WhatsApp üzerinden OTP doğrulama mesajları" />
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <Toggle enabled={settings.sms_notifications} onChange={(v) => setSettings({ ...settings, sms_notifications: v })} label="SMS Bildirimleri" description="Kritik güvenlik olayları için SMS (henüz entegre edilmedi)" disabled={true} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Güvenlik Ayarları</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">API Rate Limit (req/dk)</label>
                  <input type="number" value={settings.rate_limit_per_minute} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings({ ...settings, rate_limit_per_minute: parseInt(e.target.value) || 60 })} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500" />
                  <p className="text-xs text-gray-400 mt-1">IP başına dakikada izin verilen istek sayısı</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Auth Rate Limit (req/dk)</label>
                  <input type="number" value={settings.auth_rate_limit_per_minute} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings({ ...settings, auth_rate_limit_per_minute: parseInt(e.target.value) || 10 })} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500" />
                  <p className="text-xs text-gray-400 mt-1">Login/register endpoint'leri için sıkı limit</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Maks. Başarısız Giriş</label>
                  <input type="number" value={settings.max_login_attempts} onChange={(e) => setSettings({ ...settings, max_login_attempts: parseInt(e.target.value) || 5 })} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500" />
                  <p className="text-xs text-gray-400 mt-1">Bu sayıdan sonra hesap geçici kilitlenir</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kilitleme Süresi (dk)</label>
                  <input type="number" value={settings.lockout_duration_minutes} onChange={(e) => setSettings({ ...settings, lockout_duration_minutes: parseInt(e.target.value) || 15 })} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500" />
                  <p className="text-xs text-gray-400 mt-1">Başarısız giriş sonrası bekleme süresi</p>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Aktif Güvenlik Katmanları</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {['CORS', 'XSS Headers', 'HSTS', 'Rate Limiting', 'Security Headers', 'ORM', 'JWT Auth', 'CSP'].map(n => (
                    <div key={n} className="p-2 bg-gray-50 rounded-xl text-center">
                      <p className="text-xs text-gray-600">{n}</p>
                      <p className="text-xs font-medium text-green-600">✓ Aktif</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'api' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">API & Email Konfigürasyonu</h2>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">config.py — read-only</span>
              </div>
              {/* Editable Email Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Sağlayıcı (DB)</label>
                  <select value={settings.smtp_provider} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSettings({ ...settings, smtp_provider: e.target.value })} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500">
                    <option value="godaddy">GoDaddy SMTP</option>
                    <option value="ses">Amazon SES</option>
                    <option value="gmail">Gmail SMTP</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gönderen Adres (DB)</label>
                  <input type="email" value={settings.email_from} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings({ ...settings, email_from: e.target.value })} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500" />
                </div>
              </div>
              {/* Read-only Config */}
              {apiConfig && (
                <div className="space-y-3">
                  <ConfigRow label="Debug" value={apiConfig.debug ? 'Aktif' : 'Kapalı'} color={apiConfig.debug ? 'red' : 'green'} />
                  <ConfigRow label="Token Süresi" value={`${apiConfig.access_token_expire_minutes} dk`} />
                  <ConfigRow label="Refresh Token" value={`${apiConfig.refresh_token_expire_days} gün`} />
                  <ConfigRow label="Remember Me" value={`${apiConfig.remember_me_expire_days} gün`} />
                  <ConfigRow label="Konum Güncelleme" value={`${apiConfig.location_update_interval} sn`} />
                  <ConfigRow label="Konum Geçmişi" value={`${apiConfig.location_history_days} gün`} />
                  <ConfigRow label="APNS" value={apiConfig.apns_enabled ? (apiConfig.apns_use_sandbox ? 'Sandbox' : 'Production') : 'Kapalı'} color={apiConfig.apns_enabled ? 'green' : 'gray'} />
                  <ConfigRow label="Bundle ID" value={apiConfig.apns_bundle_id} />
                  <ConfigRow label="FCM" value={apiConfig.fcm_enabled ? 'Aktif' : 'Kapalı'} color={apiConfig.fcm_enabled ? 'green' : 'gray'} />
                  <ConfigRow label="WhatsApp" value={apiConfig.whatsapp_enabled ? 'Aktif' : 'Kapalı'} color={apiConfig.whatsapp_enabled ? 'green' : 'gray'} />
                  <ConfigRow label="Algorithm" value={apiConfig.algorithm} />
                  <ConfigRow label="Veritabanı" value={apiConfig.database_url} />
                  <ConfigRow label="Redis" value={apiConfig.redis_url} />
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <p className="text-xs font-medium text-gray-500 mb-2">CORS Origins ({apiConfig.allowed_origins.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {apiConfig.allowed_origins.map((o: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-white rounded text-xs font-mono text-gray-600 border">{o}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="text-sm font-medium text-blue-800 mb-2">Email Şablonları (7 Aktif)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {['OTP Doğrulama', 'Hoşgeldin', 'Şifre Sıfırlama', 'Hesap Silme', 'Premium', 'Ses Uyarısı', 'Feedback'].map(t => (
                    <div key={t} className="px-3 py-2 bg-white rounded-lg text-xs text-blue-700 text-center">{t}</div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'system' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Sistem & Yedekleme</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-purple-50 rounded-xl border border-purple-200 space-y-3">
                  <Toggle enabled={settings.auto_backup_enabled} onChange={(v) => setSettings({ ...settings, auto_backup_enabled: v })} label="Otomatik Yedekleme" description="Zamanlı PostgreSQL yedeklemesi" size="sm" />
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-xs text-gray-500">Frekans</label>
                      <div className="flex items-center gap-1">
                        <input type="number" value={settings.backup_frequency} onChange={(e) => setSettings({ ...settings, backup_frequency: parseInt(e.target.value) || 24 })} className="w-full px-3 py-2 bg-white rounded-lg text-sm outline-none" />
                        <span className="text-xs text-gray-500 whitespace-nowrap">saat</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-gray-500">Saklama</label>
                      <div className="flex items-center gap-1">
                        <input type="number" value={settings.backup_retention_days} onChange={(e) => setSettings({ ...settings, backup_retention_days: parseInt(e.target.value) || 30 })} className="w-full px-3 py-2 bg-white rounded-lg text-sm outline-none" />
                        <span className="text-xs text-gray-500 whitespace-nowrap">gün</span>
                      </div>
                    </div>
                  </div>
                </div>
                <button className="p-4 bg-blue-50 rounded-xl border border-blue-200 hover:bg-blue-100 transition-colors text-left">
                  <div className="flex items-center gap-3">
                    <Download className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-800">Şimdi Yedekle</p>
                      <p className="text-xs text-blue-600">Manuel yedekleme başlat</p>
                    </div>
                  </div>
                </button>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <h3 className="font-medium text-gray-800 mb-2">Veritabanı</h3>
                  <p className="text-xs text-gray-600">Durum: <span className={`font-medium ${health?.database_status === 'healthy' ? 'text-green-600' : 'text-red-500'}`}>{health?.database_status || '-'}</span></p>
                  <p className="text-xs text-gray-400 mt-1">PostgreSQL — Hetzner</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <h3 className="font-medium text-gray-800 mb-2">Redis Cache</h3>
                  <p className="text-xs text-gray-600">Durum: <span className={`font-medium ${health?.redis_status === 'healthy' ? 'text-green-600' : 'text-red-500'}`}>{health?.redis_status || '-'}</span></p>
                  <p className="text-xs text-gray-400 mt-1">OTP, rate limit, session cache</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Settings;
